import { useTranslation } from "react-i18next";

import type { Calibration, CalibrationScope } from "@/api/calibration";

/**
 * O que a Calibragem precisa saber do escopo aprovado antes de publicar (21 e
 * 22/10/2026).
 *
 * Na AL5 PS4 a publicação contava 66 scope items com o escopo aprovado em 43,
 * e nada na tela avisava. Desde que a lista é o Fit-to-Standard, ela segue a
 * aprovação sozinha, e a caixa diz:
 *
 * - que não há escopo aprovado, e por isso nada se recalcula nem se publica;
 * - o que sai e o que entra na lista ao publicar de novo, comparado com a
 *   seleção publicada. Vale também para a publicação anterior ao carimbo, que
 *   é a que contava item fora do escopo;
 * - o item Ativar que o catálogo de esforço não tem, que conta pelos dias de
 *   referência da banda escolhida.
 */
export function ScopeDriftNotice({
  calibration,
  scope,
}: {
  calibration: Calibration | undefined;
  scope: CalibrationScope;
}) {
  const { t } = useTranslation();
  const notApproved = scope.approval_version == null;
  const left = calibration?.scope_left ?? [];
  const entered = calibration?.scope_entered ?? [];
  const changed = !notApproved && (left.length > 0 || entered.length > 0);
  const stamped = calibration?.scope_approval_version ?? null;
  const withoutEffort = scope.without_effort ?? [];

  if (!notApproved && !changed && withoutEffort.length === 0) return null;

  const list = (codes: string[]) => codes.join(", ");

  return (
    <div
      data-testid="calibration-scope-drift"
      className="space-y-1.5 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900"
    >
      {notApproved && (
        <p>
          {t(
            "There is no approved scope in the Functional (it was reopened or never approved). Approve the scope to recalculate and publish the calibration.",
          )}
        </p>
      )}
      {changed && (
        <>
          <p>
            {stamped != null && stamped !== scope.approval_version
              ? t(
                  "The approved scope changed since the publication (v{{from}} → v{{to}}). Publishing again updates the calibration list.",
                  { from: stamped, to: scope.approval_version },
                )
              : t(
                  "Today's list is different from the published version. Publishing again updates the calibration.",
                )}
          </p>
          {left.length > 0 && (
            <p className="text-xs">
              {t("Leave the list ({{count}}): {{codes}}", { count: left.length, codes: list(left) })}
            </p>
          )}
          {entered.length > 0 && (
            <p className="text-xs">
              {t("Enter the list, to review ({{count}}): {{codes}}", {
                count: entered.length,
                codes: list(entered),
              })}
            </p>
          )}
        </>
      )}
      {withoutEffort.length > 0 && (
        <p className="text-xs">
          {t("Not in the effort catalogue, counted with the reference days of the chosen complexity ({{count}}): {{codes}}", {
            count: withoutEffort.length,
            codes: list(withoutEffort),
          })}
        </p>
      )}
    </div>
  );
}
